import "assets/css/grid.scss";
import "assets/css/grid.scss";
import "assets/css/style.scss";
import "assets/css/dashboard.scss";
import "./Offers.scss";
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as benefitApis from 'apis/benefits';
import ViewMore from "components/common/ViewMore";
import BannerInner from "components/common/Bannerinner";
import OfferCard from "components/common/OfferCard";
import OfferFilters, { OFFER_SORTS } from "components/common/OfferFilters";

const SKELETON_COUNT = 8;

function Offers() {
  // The filters live in the URL so a filtered listing can be reloaded or
  // shared, and so the selection survives navigating to an offer and back.
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') === OFFER_SORTS.LATEST
    ? OFFER_SORTS.LATEST
    : OFFER_SORTS.TOP_RATED;

  const [benefits, setBenefits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await benefitApis.fetchBenefitCategories();
        setCategories(response.data || []);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    // Guards against a slower earlier request landing after a newer one and
    // overwriting the results of the filter the user actually has selected.
    let ignore = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await benefitApis.fetchUserBenefits({ category, sort });
        if (!ignore) setBenefits(response.data || []);
      } catch (error) {
        console.log(error);
        if (!ignore) setBenefits([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();

    return () => { ignore = true; };
  }, [category, sort]);

  // Only non-default values go into the URL, so the unfiltered listing stays
  // on a clean /dashboard/offers.
  const updateFilters = ({ category: nextCategory = category, sort: nextSort = sort }) => {
    const params = {};
    if (nextCategory) params.category = nextCategory;
    if (nextSort !== OFFER_SORTS.TOP_RATED) params.sort = nextSort;
    setSearchParams(params, { replace: true });
  };

  const showSkeletons = loading && benefits.length === 0;

  return (
    <div className="offers-page">
      <BannerInner />
      <div className="container-fluid">
        <div className="container offers-listing">

          <OfferFilters
            categories={categories}
            activeCategory={category}
            onCategoryChange={(value) => updateFilters({ category: value })}
            sort={sort}
            onSortChange={(value) => updateFilters({ sort: value })}
            resultCount={benefits.length}
            loading={loading}
          />

          <div className={`offers-grid ${loading && !showSkeletons ? 'offers-grid-loading' : ''}`}>
            {showSkeletons && (
              Array.from({ length: SKELETON_COUNT }, (_, index) => (
                <div key={`skeleton-${index}`} className="offers-skeleton" />
              ))
            )}

            {!loading && benefits.length === 0 && (
              <div className="offers-empty">
                <i className="fa-solid fa-tags" />
                <h4>No offers here yet</h4>
                <p>
                  {category
                    ? `There are no offers under "${category}" right now. Try another category.`
                    : 'New offers are added regularly - check back soon.'}
                </p>
                {category && (
                  <button type="button" onClick={() => updateFilters({ category: '' })}>
                    View all offers
                  </button>
                )}
              </div>
            )}

            {
              // OfferCard sized and wrapped like live-test.html. The legacy
              // card is kept further down in this file, unused, as a
              // quick fallback in case we need to revert.
              !showSkeletons && benefits.map(benefit => (
                <OfferCard key={benefit.id} benefit={benefit} />
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}


// Legacy tile, kept as a backup while OfferCard (the redesigned tile) is
// rolled out above. Not currently rendered anywhere.
// eslint-disable-next-line no-unused-vars
const OffersCardLegacy = ({ benefit }) => {
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;

  return (
    <div key={benefit.id} className="col-lg-3 col-md-12 col-sm-12 p-150">

      <div className="offer-box relative h-full br-t-200">

        <img src={`${assetUrl}${benefit?.images?.[0]}`} alt="" />

        <h3 className="ff-inter-medium fw-medium fs-300 lh-300 text-black px-200 pt-200">
          {benefit.name}
        </h3>

        <div className="mt-50 ml-200 category ff-inter fs-150 bg-bright-red text-white d-block br-50 py-25 px-50">{benefit.category}</div>

        <div style={{ 'min-height': '96px' }} className="ff-inter-medium text-black fw-medium my-200 fs-200 lh-300 text-black px-200">
          <ViewMore lines={4}>{benefit.description}</ViewMore>
          <div style= {{'padding-top':'30px'}}><strong className="text-green ff-inter-semibold fs-semibold pt-30">{benefit?.offerCode}</strong>
</div>
                  </div>

        <div className="flex flex-wrap flex-row jc-space-between px-200">
          <a className="bw-25 primary-hover mb-200 px-200 py-100 fs-150 ff-inter-medium lh-200 text-bright-red bg-white br-100 border bc-bright-red" href={`/dashboard/offers/${benefit.id}`}>Details</a>
          <a className="bw-25 primary-hover mb-200 px-200 py-100 fs-150 ff-inter-medium lh-200 text-bright-red bg-white br-100 border bc-bright-red" href={`${benefit?.partner?.website}`}>Visit Website</a>
        </div>

      </div>

    </div>
  )
};

export default Offers;
